<script setup>
// Astra DS · GridCell — la celda de retícula.
//
// Figma: Pattern/GridCell 678:525. (⚠️ La bitácora daba `658:4092`, que ya no
// existe: el componente se rehizo en F5 bis. El id bueno sale de leer una
// instancia de la plantilla.)
//
// 🔴 **El arte va a 4:3, que no es lo que decían ni el nodo ni el prototipo.**
//
// El nodo dice «el arte OCUPA el alto sobrante», que en Figma era una
// limitación —no se pueden redimensionar nodos anidados en una instancia—. El
// prototipo le daba **190px fijos** (`.card__art`), y con la regla del 24.08
// ganó el prototipo. Eso valió mientras la imagen era el archivo completo de
// WordPress y daba igual por dónde cortarla.
//
// Dejó de valer el 26.08, cuando la fuente pasó a ser `actualidad.json` y la
// imagen llegó ya preparada por la redacción a 800×600: a 190px de alto la
// celda enseñaba una banda de 2,52:1 y se comía el 47% de la fotografía.
// Encargo de Pablo, y es lo razonable — la imagen se enseña entera.
//
// Lo que se conserva es el recorte por ABAJO para cuando aun así sobre alto
// —hay destacadas cuadradas—: el anclaje arriba es el defecto de DuotoneArt
// justo por esto.
//
// Los bordes van a la derecha y abajo, y la rejilla cierra por la izquierda con
// el `padding-left: 1px` de su contenedor. Así no hay línea doble donde dos
// celdas se tocan.
import { computed } from "vue";
import DuotoneArt from "./DuotoneArt.vue";
import MiniWave from "./MiniWave.vue";
import SignalDot from "./SignalDot.vue";

const props = defineProps({
  kicker: { type: String, default: "" },
  // La esquina derecha del kicker: formato, duración, lo que la vista quiera.
  kickerEnd: { type: String, default: "" },
  title: { type: String, required: true },
  meta: { type: String, default: "" },

  /**
   * Una segunda línea de meta, debajo de la primera.
   *
   * 🔴 La gana el componente el 21.09.26 con Butaca (`Pattern/GridCell 678:525`):
   * la tarjeta de una secuencia necesita decir **el artista y, debajo, la
   * película con su año**, y con una sola línea había que elegir. Va en
   * `mono/micro` y `text/secondary`, un punto por encima de la primera, porque
   * es el dato que distingue una sala de la otra.
   */
  meta2: { type: String, default: "" },

  // Proporción del arte. **4:3 por defecto**, que es como llegan las imágenes
  // de actualidad y de programas.
  //
  // Se puede cambiar porque no todas las fuentes dan la misma forma: las
  // miniaturas de TV son fotogramas 16:9, y meterlas en un 4:3 obligaría a
  // recortarles un cuarto a lo ancho. La retícula manda en el ancho de la
  // celda, que es lo que la hace retícula; el alto del arte lo pone lo que hay
  // dentro.
  artRatio: { type: String, default: "4 / 3" },

  image: { type: String, default: "" },
  // Por dónde se recorta el arte cuando sobra alto. **Arriba por defecto**, que
  // es lo correcto en una fotografía: si algo se pierde, que se pierda por el
  // pie. Se centra donde la imagen no es una fotografía sino un fotograma que
  // ya viene centrado en su propio marco, como las miniaturas de YouTube.
  imagePosition: { type: String, default: "top" },
  // A qué volver si `image` es una URL derivada que no existe.
  imageFallback: { type: String, default: "" },
  imageAlt: { type: String, default: "" },
  // Etiqueta de esquina sobre el arte, para cuando el formato pesa más que el
  // género —«Álbum», «EP»— y no cabe en el kicker.
  tag: { type: String, default: "" },

  // La tarjeta del programa que está sonando ahora enseña la onda mínima en la
  // esquina superior derecha. No se deduce de `tag`: que la etiqueta diga «En
  // emisión» es una coincidencia de texto, y de un texto no se cuelga un
  // comportamiento.
  live: { type: Boolean, default: false },
  playing: { type: Boolean, default: false },
  levels: { type: Array, default: () => [] },
  analisis: { type: String, default: "esperando" },
  showArt: { type: Boolean, default: true },

  to: { type: [String, Object], default: null },
  href: { type: String, default: "" },

  // La celda abre algo en la propia página en vez de llevar a otra: hoy, la
  // ficha del artista de rotación desde Lanzamientos 2026. Es un botón de
  // verdad, no un div con `@click`: así entra en el tabulador, responde a
  // Enter y al espacio, y se anuncia como lo que es. Quien la monta escucha
  // `@click`. `to` manda: un destino real siempre gana a una acción.
  accion: { type: Boolean, default: false },
});

const tag = computed(() => {
  if (props.to) return "router-link";
  if (props.href) return "a";
  if (props.accion) return "button";
  return "div";
});

const linkProps = computed(() => {
  if (props.to) return { to: props.to };
  if (props.href) return { href: props.href };
  if (props.accion) return { type: "button" };
  return {};
});
</script>

<template>
  <component :is="tag" class="grid-cell" v-bind="linkProps">
    <div v-if="showArt" class="grid-cell__art" :style="{ aspectRatio: artRatio }">
      <!-- El arte se puede sustituir entero: TV mete ahí su reproductor, que
           no es una imagen sino una portada que se convierte en vídeo. -->
      <slot name="art">
        <DuotoneArt
          :src="image"
          :fallback="imageFallback"
          :alt="imageAlt || title"
          :tag="live ? '' : props.tag"
          :live="live && playing"
          ratio="auto"
          :position="imagePosition"
        />
      </slot>

      <!-- 🔴 **El programa en emisión lleva el mismo chip que `OnAirShow`**,
           pegado al pie del arte y con la onda dentro. Encargo de Pablo,
           30.09.26: «unifica». Hasta entonces esta celda decía lo mismo con
           dos piezas distintas —la etiqueta de esquina y la onda suelta arriba
           a la derecha— y no se parecía a la ficha de arriba. -->
      <p v-if="live" class="grid-cell__chip">
        <MiniWave v-if="analisis !== 'no'" :levels="levels" :playing="playing" />
        <SignalDot v-else :playing="playing" />
        <span>{{ props.tag || "En emisión" }}</span>
      </p>
    </div>

    <div class="grid-cell__body">
      <p v-if="kicker || kickerEnd" class="grid-cell__kicker">
        <span>{{ kicker }}</span>
        <span>{{ kickerEnd }}</span>
      </p>
      <h3 class="grid-cell__title">{{ title }}</h3>
      <p v-if="meta" class="grid-cell__meta">{{ meta }}</p>
      <p v-if="meta2" class="grid-cell__meta2">{{ meta2 }}</p>
    </div>

  </component>
</template>

<style scoped lang="scss">
@use "@/assets/scss/interaccion" as i;
@use "@/assets/scss/type" as t;

// ⚠️ **Solo la celda que navega.** La misma celda se monta como `div` cuando no
// tiene destino —un listado que solo informa—, y ahí el color estaría
// prometiendo un clic que no existe.
a.grid-cell,
button.grid-cell {
  // El patrón de tarjeta: arte a color y llamada con el filete marcado.
  @include i.tarjeta;
}

.grid-cell {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-width: 0;
  // 🔴 **La celda ya no dibuja filetes** (21.09.26). Los ponía a su derecha y
  // abajo, y eso obligaba a cada rejilla a compensar: `padding-left: 1px` para
  // cerrar la primera columna, `border-right: 0` en la última de cada fila y un
  // cierre propio del módulo que doblaba con la última fila. Ahora las juntas
  // son el hueco de la rejilla: ver `scss/_estructura.scss`.
  //
  // ⚠️ **El lienzo es obligatorio**, y por eso está aquí: es lo que tapa el
  // color de la junta dentro de la celda.
  background: var(--color-bg-canvas);
  color: inherit;
  text-decoration: none;
}

// El arte se come lo que sobra del alto de la celda. `min-height: 0` porque sin
// él un hijo flexible no encoge por debajo de su contenido y la imagen empuja
// la celda.
// **4:3, la proporción de la imagen.** Encargo de Pablo el 26.08, al ver el
// archivo con la fuente nueva.
//
// El prototipo daba al arte 190px fijos, y con ellos la celda enseñaba una
// BANDA en vez de una imagen: 479 de ancho a 190 de alto son 2,52:1, y lo que
// manda la redacción es 4:3 —800×600 desde `actualidad.json`—, así que se
// perdía el 47% del alto. Aquella medida venía de cuando la imagen era el
// archivo completo de WordPress y daba igual por dónde cortarla; ahora la
// imagen ES la que el servidor ha preparado, y se enseña entera.
//
// Sigue habiendo `overflow: hidden` y anclaje arriba: una destacada puede
// llegar cuadrada —las hay— y entonces sí sobra alto, que se va por abajo.
.grid-cell__art {
  position: relative;
  flex: none;
  overflow: hidden;
}

.grid-cell__art :deep(.duotone) {
  height: 100%;
}

// Crece hasta el pie de la celda para que la fecha de todas las celdas de una
// fila quede a la misma altura.
// Medidas del master, leídas del nodo `Pattern/GridCell 678:525` el 28.08.26:
// cuerpo de 93 de alto, contenido a 14 de los lados, 13 arriba, 18 abajo y 7
// entre líneas.
//
// 🔴 El lateral estaba en **28, el doble**. En las celdas anchas no cantaba,
// pero en las de 240 —los discos de Radio— el kicker se quedaba sin sitio y el
// texto parecía metido con calzador. Encargo de Pablo: alinear con el master.
.grid-cell__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 7px;
  padding: 13px 14px 18px;
}

// El chip de emisión, copia del de `OnAirShow`: rojo, abajo a la izquierda y
// apoyado en el borde del arte, con el mismo par de tokens —5,62:1 en los dos
// temas—. Sustituye al punto suelto de arriba a la derecha (28.08.26).
.grid-cell__chip {
  @include t.mono-micro;
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 7px 14px;
  background: var(--color-bg-accent);
  color: var(--color-text-on-action);
}

.grid-cell__kicker {
  @include t.mono-micro;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  // En rojo, como en el prototipo: en el archivo el tipo de pieza es lo que
  // ordena la lectura. (El nodo lo da en `text/tertiary`; manda el prototipo.)
  color: var(--color-text-accent);
  white-space: nowrap;
}


.grid-cell__title {
  @include t.display-item;
  margin: 0;
  color: var(--color-text-primary);
  transition: color 0.12s linear;
}

.grid-cell__meta {
  @include t.mono-nano;
  margin: auto 0 0;
  color: var(--color-text-secondary);
}

// La segunda línea NO se pega abajo con `auto`: se queda junto a la primera, o
// las dos se separarían y parecerían datos distintos.
.grid-cell__meta2 {
  @include t.mono-micro;
  margin: 2px 0 0;
  color: var(--color-text-secondary);
}


// 🔴 **Aquí el titular se teñía de rojo al pasar por encima, y se retira**
// (Pablo, 18.09.26). La tarjeta entera ya se ha encendido con el arte; teñir
// además el titular es decir dos veces lo mismo, y con el rojo, que en este
// sistema significa acción. La celda se comporta como la puerta.

// La celda que abre algo: el navegador le pone tipografía, alineación y relleno
// propios al botón, y sin esto la misma celda se lee distinta según lleve a una
// página o abra una ficha.
button.grid-cell {
  padding: 0;
  border-top: 0;
  border-left: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  appearance: none;

  // 🔴 **Los hijos, a todo el ancho.** Un botón encoge su contenido y lo
  // centra, y `text-align` no lo evita: el cuerpo medía lo que su título. Con
  // un título largo llenaba la celda y parecía alineado; con uno corto se
  // quedaba en medio, y la fila de Lanzamientos salía con sangrías distintas
  // en cada disco. Visto por Pablo el 17.09.26.
  > * {
    box-sizing: border-box;
    width: 100%;
  }

  // El anillo azul del navegador no es del sistema: el foco es el filete rojo
  // de dos píxeles hacia dentro, como en el resto de controles.
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .grid-cell__title {
    transition: none;
  }
}

// ── El margen móvil ──
//
// 20 a cada lado, como el resto del sistema por debajo de 880. Este bloque se
// había quedado con el de escritorio, y en una pantalla de 390 convivían tres
// verticales distintas —20, 24 y 28— según de qué componente fuera cada caja.
// Se lee como desalineación aunque cada bloque, por separado, esté bien.
//
// Mismo corte que ModuleTitleBar, que era el único que ya lo hacía.
@media (max-width: 880px) {
  .grid-cell__body {
    padding: 14px 20px 18px;
  }
}
</style>
